import React from "react";
import {
  FaUndoAlt,
  FaClipboardCheck,
  FaBoxOpen,
  FaMoneyCheckAlt,
  FaTimesCircle,
  FaShippingFast,
  FaExclamationTriangle,
  FaHeadset,
} from "react-icons/fa";

const RefundPolicy = () => {
  const sections = [
    {
      icon: <FaClipboardCheck />,
      title: "Refund Eligibility",
      content:
        "At Glamira, customer satisfaction is important to us. If you receive an incorrect, damaged, defective, or eligible returnable product, you may request a refund or replacement in accordance with this policy.",
    },
    {
      icon: <FaUndoAlt />,
      title: "Return Window",
      list: [
        "Eligible products may be returned within the return period mentioned on the product page.",
        "The item must be unused, in its original packaging, and accompanied by all accessories and invoices.",
        "Products showing signs of misuse or damage caused by the customer may not qualify for returns.",
      ],
    },
    {
      icon: <FaBoxOpen />,
      title: "Non-Returnable Products",
      list: [
        "Products marked as non-returnable on the product page.",
        "Personal care, hygiene, or customized items where applicable.",
        "Products damaged due to improper use after delivery.",
        "Items returned without original packaging or accessories.",
      ],
    },
    {
      icon: <FaMoneyCheckAlt />,
      title: "Refund Process",
      list: [
        "After receiving and inspecting the returned product, eligible refunds will be approved.",
        "Refunds are generally processed within 7-10 business days after successful verification.",
        "Refunds will be credited to the original payment method or as per applicable payment partner guidelines.",
      ],
    },
    {
      icon: <FaShippingFast />,
      title: "Order Cancellation",
      content:
        "Orders may be cancelled before they are processed or dispatched. Once shipped, cancellation requests may not be possible. Customers can contact our support team for assistance regarding eligible cancellations.",
    },
    {
      icon: <FaTimesCircle />,
      title: "Replacement Policy",
      content:
        "If you receive a damaged, defective, or incorrect product, you may request a replacement subject to verification. Replacement availability depends on stock and product eligibility.",
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Refund Exceptions",
      list: [
        "Shipping charges may not be refundable unless the return is due to our error.",
        "Refunds may be declined if products fail quality inspection after return.",
        "Certain promotional or clearance sale items may not be eligible for refunds unless required by law.",
      ],
    },
    {
      icon: <FaHeadset />,
      title: "Customer Support",
      content:
        "For refund, return, or cancellation assistance, customers may contact our support team through the Contact Us page with their order details and supporting information.",
    },
  ];

  return (
    <div
      style={{
        background: "#f5f7fb",
        minHeight: "100vh",
        padding: "60px 0",
      }}
    >
      <div className="container">

        {/* Hero Section */}

        <div
          className="text-center text-white rounded-4 shadow-lg p-5 mb-5"
          style={{
            background:
              "linear-gradient(135deg,#0f172a 0%,#16a34a 100%)",
          }}
        >
          <h1 className="display-4 fw-bold">
            Refund & Cancellation Policy
          </h1>

          <p className="lead mt-3 mb-0">
            We strive to make every purchase worry-free with a transparent
            return, refund, and cancellation process.
          </p>
        </div>

        {/* Introduction */}

        <div className="card border-0 shadow-sm rounded-4 mb-5">
          <div className="card-body p-5">

            <h3 className="fw-bold mb-3">
              Our Commitment
            </h3>

            <p className="text-muted mb-0">
              At Glamira, we are committed to providing customers with quality
              products and a reliable shopping experience. If something doesn't
              go as expected, our refund and cancellation policy is designed to
              resolve issues fairly and efficiently while maintaining
              transparency throughout the process.
            </p>

          </div>
        </div>

        {/* Policy Cards */}

        <div className="row">
          {sections.map((section, index) => (
            <div className="col-lg-6 mb-4" key={index}>
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4">

                  <div className="d-flex align-items-center mb-3">

                    <div
                      className="rounded-circle text-white d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: 55,
                        height: 55,
                        background:
                          "linear-gradient(135deg,#16a34a,#15803d)",
                        fontSize: 22,
                      }}
                    >
                      {section.icon}
                    </div>

                    <div>
                      <small className="text-success fw-bold">
                        Section {index + 1}
                      </small>

                      <h5 className="fw-bold mb-0">
                        {section.title}
                      </h5>
                    </div>

                  </div>

                  {section.content && (
                    <p className="text-muted mb-0">
                      {section.content}
                    </p>
                  )}

                  {section.list && (
                    <ul className="text-muted ps-3 mb-0">
                      {section.list.map((item, i) => (
                        <li key={i} className="mb-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}

        <div className="card border-0 shadow-lg rounded-4 mt-5">
          <div className="card-body p-5">

            <h2 className="fw-bold text-center mb-5">
              Refund Process
            </h2>

            <div className="row text-center">

              <div className="col-md-3 mb-4">
                <h2>1️⃣</h2>
                <h5>Request</h5>
                <p className="text-muted">
                  Submit your refund or return request through customer support.
                </p>
              </div>

              <div className="col-md-3 mb-4">
                <h2>2️⃣</h2>
                <h5>Verification</h5>
                <p className="text-muted">
                  Our team reviews the request and verifies product eligibility.
                </p>
              </div>

              <div className="col-md-3 mb-4">
                <h2>3️⃣</h2>
                <h5>Approval</h5>
                <p className="text-muted">
                  Once approved, the refund or replacement process begins.
                </p>
              </div>

              <div className="col-md-3 mb-4">
                <h2>4️⃣</h2>
                <h5>Completion</h5>
                <p className="text-muted">
                  Refund is processed to the original payment method where
                  applicable.
                </p>
              </div>

            </div>

          </div>
        </div>

        {/* Important Notice */}

        <div
          className="mt-5 p-4 rounded-4 shadow-sm"
          style={{
            background: "#fff7ed",
            borderLeft: "6px solid #ea580c",
          }}
        >
          <h4 className="fw-bold mb-3">
            ⚠️ Important Information
          </h4>

          <p className="mb-0 text-muted">
            Refund approval is subject to product inspection and compliance
            with this policy. Glamira reserves the right to decline refund
            requests that do not meet the applicable eligibility criteria or
            show evidence of misuse, damage, or policy violations.
          </p>
        </div>

        {/* CTA */}

        <div className="card border-0 shadow-lg rounded-4 mt-5">
          <div className="card-body text-center p-5">

            <h2 className="fw-bold mb-3">
              Need Assistance?
            </h2>

            <p
              className="text-muted mx-auto"
              style={{ maxWidth: "700px" }}
            >
              If you have questions regarding cancellations, returns, or
              refunds, our customer support team is available to help you
              throughout the process.
            </p>

            <a
              href="/contact"
              className="btn btn-success btn-lg px-5 mt-3"
            >
              Contact Support
            </a>

          </div>
        </div>

        {/* Footer */}

        <div className="text-center mt-5 pt-4 border-top">

          <h5 className="fw-bold">
            Shop With Confidence
          </h5>

          <p className="text-muted mb-2">
            Our goal is to provide a fair, transparent, and customer-friendly
            refund process while ensuring the integrity of our marketplace.
          </p>

          <small className="text-secondary">
            © {new Date().getFullYear()} Glamira. All Rights Reserved.
          </small>

        </div>

      </div>
    </div>
  );
};

export default RefundPolicy;